import { IUpdateWardConfigurationRequest } from "../../../../@types/admin/AdminWard";

/**
 * Catalogue for the ward configuration form (EN-5195). Closed lists on
 * purpose: the API branches on these exact strings (`OnboardingStrategyType`,
 * `SmartDeviceType` / `MeasurementType` descriptions), so a catalogue endpoint
 * would add a round-trip without adding truth.
 */

/** Boolean flags of `UpdateWardConfigurationRequestDto`. */
export type TWardFeatureFlagKey = keyof Pick<
  IUpdateWardConfigurationRequest,
  | "enablePatientMessaging"
  | "enablePatientMedication"
  | "enablePatientAddMedication"
  | "enableMedicationSetupAction"
  | "enableFibricheckIntegration"
  | "enableLabResults"
  | "enableAddingLabResults"
  | "enableNoAppEnrolment"
  | "enableHealthDataSteps"
  | "enableAddDeviceOnEnrolment"
  | "enableTreatments"
  | "enableISHLTBaselines"
  | "enableDexcomIntegration"
  | "enableGoogleHealthIntegration"
  | "enableScheduledActionNotifications"
  | "enableEMedIntegration"
  | "enableAliveCorEcgIntegration"
  | "enablePassioIntegration"
  | "enableTmpIntegration"
>;

export interface IWardFeatureSwitch {
  key: TWardFeatureFlagKey;
  label: string;
  /** Tooltip copy: what turning this on actually does, for an operator. */
  description: string;
}

export interface IWardFeatureSwitchGroup {
  title: string;
  switches: IWardFeatureSwitch[];
}

/**
 * The flags, grouped by what they affect instead of Retool's single flat run
 * of toggles. Each description feeds the flag's info tooltip.
 */
export const WARD_FEATURE_SWITCH_GROUPS: IWardFeatureSwitchGroup[] = [
  {
    // EN-5267: the three medication switches build on each other, so they sit
    // together and turn each other on and off (see applyWardFlagChange).
    title: "Medications",
    switches: [
      {
        key: "enablePatientMedication",
        label: "Patient medication",
        description:
          "Shows the Medications section in the patient app: the medications the clinical team or an integration (e.g. the eMed list) sent for the patient, and changes to them. Turning it off also turns off the two switches below.",
      },
      {
        key: "enablePatientAddMedication",
        label: "Patients can add medications",
        description:
          "Also lets patients add, edit and remove their own medications, picked from the hospital's medication source, with a daily checklist to log intake. Turns on Patient medication.",
      },
      {
        key: "enableMedicationSetupAction",
        label: "Medication setup action",
        description:
          'Shows patients a "Setup medication" action on the app home screen until they add their first medication. Turn it off to keep adding medications available without prompting for it. Turns on Patients can add medications.',
      },
    ],
  },
  {
    title: "Patient app features",
    switches: [
      {
        key: "enablePatientMessaging",
        label: "Patient messaging",
        description:
          "Lets patients exchange messages with the clinical team in the app, and shows the conversation to clinicians in the portal.",
      },
      {
        key: "enableTreatments",
        label: "Treatments",
        description:
          "Enables the treatments feature (e.g. Aerogen nebulizer sessions). The Aerogen Nebulizer device is only offered while this is on.",
      },
      {
        key: "enableHealthDataSteps",
        label: "Health data steps",
        description:
          "Collects daily step counts from the phone's health data (Apple Health / Health Connect).",
      },
      {
        key: "enableScheduledActionNotifications",
        label: "Daily action notifications",
        description:
          "Sends patients push reminders for their scheduled daily actions. Turning it off silences those reminders for the whole ward.",
      },
      {
        key: "enablePassioIntegration",
        label: "Passio nutrition",
        description:
          "Shows the Passio food-scanning nutrition feature in the patient app.",
      },
    ],
  },
  {
    title: "Results & baselines",
    switches: [
      {
        key: "enableLabResults",
        label: "Lab results",
        description: "Shows lab results to patients in the app.",
      },
      {
        key: "enableAddingLabResults",
        label: "Adding lab results",
        description: "Also lets lab results be added for this ward's patients.",
      },
      {
        key: "enableISHLTBaselines",
        label: "ISHLT home FEV1 baselines",
        description:
          "Calculates ISHLT home spirometry baselines (FEV1) for this ward's patients. Turning it on generates baselines from existing measurements straight away.",
      },
    ],
  },
  {
    title: "Onboarding & enrolment",
    switches: [
      {
        key: "enableNoAppEnrolment",
        label: "No-app enrolment",
        description:
          "Allows enrolling patients who will not use the mobile app (e.g. device-only monitoring).",
      },
      {
        key: "enableAddDeviceOnEnrolment",
        label: "Add device on enrolment",
        description:
          "Prompts to pair a measurement device as part of patient enrolment.",
      },
    ],
  },
  {
    title: "Integrations",
    switches: [
      {
        key: "enableFibricheckIntegration",
        label: "Fibricheck",
        description:
          "Enables Fibricheck heart-rhythm checks from the patient app.",
      },
      {
        key: "enableDexcomIntegration",
        label: "Dexcom",
        description:
          "Syncs Dexcom continuous glucose monitor data for this ward's patients (runs a background sync job).",
      },
      {
        key: "enableGoogleHealthIntegration",
        label: "Google Health",
        description:
          "Syncs Google Health Connect data for this ward's patients (runs a background sync job).",
      },
      {
        key: "enableAliveCorEcgIntegration",
        label: "AliveCor ECG",
        description: "Enables AliveCor ECG recordings for this ward.",
      },
      {
        key: "enableEMedIntegration",
        label: "eMed",
        description:
          "Enables the eMed integration. Requires the eMed MRN key below, which names the custom field holding the patient's MRN.",
      },
      {
        key: "enableTmpIntegration",
        label: "Transmural Platform (TMP) integration",
        description:
          "Accepts telemonitoring orders from the Transmural Platform for this ward: patients are provisioned from TMP webhooks and hospital clinicians can complete missing onboarding data through a one-time link (EN-5203).",
      },
    ],
  },
];

export const ONBOARDING_STRATEGY_OPTIONS: {
  value: "LegacyApp" | "NewApp";
  label: string;
}[] = [
  { value: "NewApp", label: "New app" },
  { value: "LegacyApp", label: "Legacy app" },
];

/** `MeasurementType` descriptions the app can chart as spirometry results. */
export const SPIROMETRY_RESULT_TYPE_OPTIONS: {
  value: string;
  label: string;
}[] = [
  { value: "fvc", label: "FVC" },
  { value: "fev1", label: "FEV1" },
  { value: "fev1_fvc", label: "FEV1/FVC" },
  { value: "calculated_predicted_fvc", label: "Predicted FVC (%)" },
  { value: "calculated_predicted_fev1", label: "Predicted FEV1 (%)" },
  { value: "z_score_fvc", label: "FVC z-score" },
  { value: "z_score_fev1", label: "FEV1 z-score" },
  { value: "pef", label: "PEF" },
  { value: "fev6", label: "FEV6" },
];

/** `SmartDeviceType` descriptions, with the API's labels. */
export const SMART_DEVICE_OPTIONS: { value: string; label: string }[] = [
  { value: "mir_spirometer", label: "MIR Spirometer" },
  { value: "nonin_oximeter", label: "Nonin Oximeter" },
  { value: "ihealth_oximeter", label: "iHealth Oximeter" },
  { value: "masimo_oximeter", label: "Masimo Oximeter" },
  { value: "bodytrace_bp", label: "BodyTrace BP Monitor" },
  { value: "bodytrace_scale", label: "BodyTrace Scale" },
  { value: "ihealth_thermometer", label: "iHealth Thermometer" },
  { value: "aerogen_nebulizer", label: "Aerogen Nebulizer" },
];

/**
 * Mirrors the API's `SmartDeviceType` constraints: the MIR spirometer must be
 * present whenever an explicit list is set (there is no manual fallback for
 * spirometry), and the oximeters are mutually exclusive.
 */
export const MANDATORY_SMART_DEVICES = ["mir_spirometer"];
export const EXCLUSIVE_OXIMETER_DEVICES = [
  "nonin_oximeter",
  "ihealth_oximeter",
  "masimo_oximeter",
];

/** The API validates 1–100 (`UpdateWardConfigurationRequestValidator`). */
export const ONE_MSTS_THRESHOLD_MIN = 1;
export const ONE_MSTS_THRESHOLD_MAX = 100;

/**
 * The custom-field keys a ward's eMed integration can read the patient MRN
 * from — the same closed list Retool's `selectEMedMrnKey` offered.
 */
export const EMED_MRN_KEY_OPTIONS = ["MR", "MR_BEAUMONT", "MR_GALWAY"];

/**
 * Applies one switch change with the medication switches' dependencies
 * (EN-5267): each builds on the one above it, so turning one on turns on what
 * it needs, and turning one off turns off what depends on it. Turning on
 * patient-added medications also turns on the setup action, matching the
 * default every existing ward got.
 */
export function applyWardFlagChange(
  flags: Record<TWardFeatureFlagKey, boolean>,
  key: TWardFeatureFlagKey,
  value: boolean
): Record<TWardFeatureFlagKey, boolean> {
  const next = { ...flags, [key]: value };

  if (value) {
    if (key === "enableMedicationSetupAction") {
      next.enablePatientAddMedication = true;
    }
    if (key === "enablePatientAddMedication") {
      next.enableMedicationSetupAction = true;
    }
    if (
      key === "enableMedicationSetupAction" ||
      key === "enablePatientAddMedication"
    ) {
      next.enablePatientMedication = true;
    }
  } else {
    if (key === "enablePatientMedication") {
      next.enablePatientAddMedication = false;
      next.enableMedicationSetupAction = false;
    }
    if (key === "enablePatientAddMedication") {
      next.enableMedicationSetupAction = false;
    }
  }

  return next;
}
